import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediabankGetFilesForUser, useMediabankMutations } from '../../Hooks/useMediabank';
import { getPathWithRootPath } from '../../Services/common';
import { getThumbPath, MediabankGetFilesParams } from '../../Services/mediabank';
import { ObjectType } from '../../Types/base';
import {
	getMediabankFileTypeById,
	MediabankFile,
	MediabankFileType,
	MediabankFileTypes,
	MediabankSettings,
	MediabankUploadLocation,
} from '../../Types/mediabank';
import { BizButton } from '../Mini/BizButton';
import BizIconButton from '../Mini/BizIconButton';
import './biz-mediabanklistuser.scss';
import { useBizMediabank } from './BizMediabankContext';
import BizInput from '../Mini/BizInput';
import { BizIcon } from '../Mini/BizIcon';

type ListModes = 'GRID' | 'LIST';

type BizMediabankListUserProps = {
	params?: MediabankGetFilesParams;
	uploadSettings?: {
		uploadLocation: MediabankUploadLocation;
		settings: MediabankSettings;
	};
	onFileClick?: (file: MediabankFile) => void;
	listModes?: ListModes[];
	title?: string;
	showFilter?: boolean;
	downloadOnFileClick?: boolean;
	showUploadButton?: boolean;
	triggerUpload?: (handler: () => void) => void;
	showDownload?: boolean;
	showUnlink?: boolean;
	showNoFilesFound?: boolean;
};

const BizMediabankListUser = ({
	params,
	uploadSettings,
	onFileClick,
	listModes,
	title,
	showFilter,
	downloadOnFileClick,
	showUploadButton,
	triggerUpload,
	showDownload,
	showUnlink,
	showNoFilesFound,
}: BizMediabankListUserProps) => {
	const { t } = useTranslation();
	const files = useMediabankGetFilesForUser(params);
	// console.log(files);
	const [listMode, setListMode] = useState<'GRID' | 'LIST'>(listModes !== undefined && listModes[0] ? listModes[0] : 'LIST');
	const [filteredFiles, setFilteredFiles] = useState<MediabankFile[] | undefined | null>(files);
	const [filterString, setFilterString] = useState<string>('');
	// const [selectedFile, setSelectedFile] = useState<MediabankFile | null>(null);

	const { openMediabank } = useBizMediabank();

	useEffect(() => {
		setFilteredFiles(files);
	}, [files]);

	useEffect(() => {
		if (triggerUpload) {
			triggerUpload(uploadClickHandler);
		}
	}, [triggerUpload]);

	const getFileTypes = () => {
		if (params?.FileTypes) {
			return params.FileTypes.map((type) => getMediabankFileTypeById(type)).filter(
				(ft): ft is MediabankFileType => ft !== undefined
			);
		} else {
			return Object.values(MediabankFileTypes);
		}
	};

	const uploadClickHandler = () => {
		openMediabank({
			callback: (newFile) => {
				console.log(newFile);
			},
			fileTypes: getFileTypes(),
			uploadLocation: uploadSettings?.uploadLocation ?? MediabankUploadLocation.Undefined,
			mode: 'UPLOAD',
			settings: uploadSettings?.settings,
		});
	};

	const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const filterInput = e.target.value.toLowerCase();
		setFilterString(filterInput);
		if (!filterInput || filterInput === '' || filterInput.length < 3) return setFilteredFiles(files);

		if (filterInput === 'doc') {
			const filesByType = files?.filter((f) => f.Type === MediabankFileTypes.Document.id) ?? [];
			if (filesByType.length > 0) return setFilteredFiles(filesByType);
		} else if (filterInput === 'img') {
			const filesByType = files?.filter((f) => f.Type === MediabankFileTypes.Image.id) ?? [];
			if (filesByType.length > 0) return setFilteredFiles(filesByType);
		}

		return setFilteredFiles(
			files?.filter((f) => f.Name.toLowerCase().includes(filterInput) || f.Description?.toLowerCase().includes(filterInput))
		);
	};

	const onFileClickHandler = (file: MediabankFile) => {
		// setSelectedFile(file);
		onFileClick?.(file);
	};

	const linkedObjectType = uploadSettings?.settings?.linkToObject?.ObjectType ?? ObjectType.None;
	const linkedObjectId = uploadSettings?.settings?.linkToObject?.ObjectId ?? 0;

	return (
		<div className='biz-mediabank-list-user'>
			<div className='file-list-toolbar'>
				{title && <div className='list-title'>{title}</div>}
				<div className='filter'>
					{showFilter && files && files?.length > 0 && (
						<BizInput placeholder={t('MEDIABANK.SEARCH')} onChange={onFilterChange} value={filterString} />
					)}
				</div>
				{listModes !== undefined && listModes.length > 1 && files && files?.length > 0 && (
					<div className='list-mode-select'>
						<BizIconButton
							noBackground={true}
							icon='CUSTOM'
							customIcon='fa-regular fa-th'
							onClick={() => setListMode('GRID')}
							disabled={listMode == 'GRID'}
						/>
						<BizIconButton
							noBackground={true}
							icon='CUSTOM'
							customIcon='fa-regular fa-list'
							onClick={() => setListMode('LIST')}
							disabled={listMode == 'LIST'}
						/>
					</div>
				)}
				{uploadSettings &&
					uploadSettings?.uploadLocation !== MediabankUploadLocation.Undefined &&
					showUploadButton !== false && <BizButton text={t('MEDIABANK.UPLOAD')} onClick={uploadClickHandler} isLead></BizButton>}
			</div>
			{showNoFilesFound && filteredFiles && filteredFiles?.length === 0 && <div className='no-files'>{t('MEDIABANK.NO_FILES_FOUND')}</div>}
			<div className={`file-list list-mode-${listMode.toLowerCase()}`}>
				{filteredFiles?.map((file) => (
					<MediabankFileListItem
						key={file.Id}
						file={file}
						linkedObjectId={linkedObjectId}
						linkedObjectType={linkedObjectType}
						onFileClick={onFileClickHandler}
						downloadOnFileClick={downloadOnFileClick}
						showDownload={showDownload}
						showUnlink={showUnlink}
					/>
				))}
			</div>
		</div>
	);
};

export default BizMediabankListUser;

type MediabankFileListItemProps = {
	file: MediabankFile;
	linkedObjectType: ObjectType;
	linkedObjectId: number;
	onFileClick?: (file: MediabankFile) => void;
	downloadOnFileClick?: boolean;
	showDownload?: boolean;
	showUnlink?: boolean;
};

function MediabankFileListItem({
	file,
	linkedObjectId,
	linkedObjectType,
	onFileClick,
	downloadOnFileClick,
	showDownload,
	showUnlink,
}: MediabankFileListItemProps) {
	const { t } = useTranslation();
	const { unlinkFileFromObject, statusStateUnlinkFromObject } = useMediabankMutations();

	const getThumb = (file: MediabankFile) => {
		if (file.Thumbnail && file.Thumbnail !== '') {
			return getThumbPath(file.Thumbnail);
		} else {
			return getThumbPath(file.FileUrl);
		}
	};

	const fileClickHandler = () => {
		if (onFileClick) {
			onFileClick(file);
		} else {
			window.open(getPathWithRootPath(file.FileUrl), '_blank');
		}
	};

	const fileRemoveHandler = () => {
		if (confirm(t('MEDIABANK.CONFIRM_REMOVE_FILE')))
			unlinkFileFromObject(file.Id, linkedObjectType, linkedObjectId);
	};

	const downloadTarget = '_blank'; // temp hack.. May be an angularJs only thing

	return (
		<div key={file.Id} className='one-file'>
			<div className='thumbnail-wrapper' onClick={fileClickHandler}>
				<img src={getThumb(file)} alt={file.Name} className='thumbnail' />
			</div>
			<span className='file-name' onClick={!downloadOnFileClick ? fileClickHandler : undefined}>
				{downloadOnFileClick && (
					<a href={getPathWithRootPath(file.FileUrl)} target={downloadTarget} download={file.Name}>
						{file.Name}
					</a>
				)}
				{!downloadOnFileClick && <>{file.Name}</>}
			</span>
			<div className='icon-buttons'>
				{showDownload && (
					<a
						href={getPathWithRootPath(file.FileUrl)}
						download={file.Name}
						target={downloadTarget}
						className='file-download'
					>
						<span className='biz-icon-btn'>
							<BizIcon icon='DOWNLOAD' />
						</span>
					</a>
				)}
				{showUnlink && linkedObjectType !== ObjectType.None && linkedObjectId > 0 && (
					<BizIconButton icon='REMOVE' onClick={fileRemoveHandler} state={statusStateUnlinkFromObject} />
				)}
			</div>
		</div>
	);
}
